// The daily reminder, by web push: the service worker, this browser's subscription and the hour
// it's saved with. The API decides when to send (services/reminders.ts there). A browser with the
// reminder on refreshes it once a day (checked whenever the site comes back into view), and whenever
// a different player opens the site on it, so the reminder stays with whoever plays here, its time
// zone stays current, and the server doesn't let it lapse.
import type { Reminder, ReminderConfig, ReminderTest } from '@wbwl/contract'
import { useEffect, useSyncExternalStore } from 'react'
import { api, ApiFailure } from './api.ts'
import { storage } from './game.ts'
import { isIos, isStandalone } from './install.ts'

export const DEFAULT_HOUR = 9
const HOUR_KEY = 'lof:reminder-hour'
/** "<local date> <player id>" of the last refresh. */
const SYNCED_KEY = 'lof:reminder-synced'

export type ReminderState =
  /** Still looking, no push in this browser, or the server has reminders off: nothing to show. */
  | { readonly kind: 'none' }
  /** iPhone or iPad in a browser tab: push works once the site is on the Home Screen. */
  | { readonly kind: 'needs-install' }
  /** Notifications are blocked for the site in the browser's settings. */
  | { readonly kind: 'blocked' }
  | { readonly kind: 'off' }
  | { readonly kind: 'on'; readonly hour: number }

let state: ReminderState = { kind: 'none' }
const listeners = new Set<() => void>()
function set(next: ReminderState): ReminderState {
  state = next
  listeners.forEach((l) => l())
  return next
}
const subscribe = (l: () => void) => {
  listeners.add(l)
  return () => {
    listeners.delete(l)
  }
}

let worker: Promise<ServiceWorkerRegistration | null> = Promise.resolve(null)
let config: Promise<string | null> | null = null
let loading: Promise<void> | null = null
/** Who's playing here, once /me has loaded. */
let player: string | null = null
// What load() found, kept so turning the reminder on can subscribe at once, inside the tap.
let registration: ServiceWorkerRegistration | null = null
let publicKey: string | null = null

const canPush = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window

const savedHour = () => {
  const h = Number(storage.get(HOUR_KEY) ?? NaN)
  return Number.isInteger(h) && h >= 0 && h < 24 ? h : null
}
const today = () => new Date().toLocaleDateString('en-CA')

/** The hour turning the reminder on uses: the last one saved here, or 9. */
export const plannedHour = () => savedHour() ?? DEFAULT_HOUR

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const b64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (base64url.length % 4)) % 4)
  return Uint8Array.from(atob(b64), (ch) => ch.charCodeAt(0))
}

/** Whether a subscription was made with this key; true when the browser doesn't say. */
function madeWith(sub: PushSubscription, key: string): boolean {
  const have = sub.options?.applicationServerKey
  if (!have) return true
  const a = new Uint8Array(have)
  const b = keyBytes(key)
  return a.length === b.length && a.every((x, i) => x === b[i])
}

const subscribeWith = (reg: ServiceWorkerRegistration, key: string) =>
  reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) })

/** Pushes the service worker said arrived, and whoever is waiting for one. */
const arrived = new Set<string>()
const waiting = new Map<string, () => void>()

/** Registers the service worker, and refreshes the reminder when the site comes back into view. Call once as the app starts. */
export function startReminders() {
  if (!('serviceWorker' in navigator)) return
  worker = navigator.serviceWorker.register('/sw.js').then(() => navigator.serviceWorker.ready, () => null)
  // An installed app can sit in the background for days without reloading.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && player) refreshReminder(player)
  })
  navigator.serviceWorker.addEventListener('message', (e: MessageEvent) => {
    const { type, id } = e.data ?? {}
    if (type !== 'reminder-received' || typeof id !== 'string') return
    arrived.add(id)
    waiting.get(id)?.()
  })
  navigator.serviceWorker.startMessages()
}

/** Saves this browser's reminder for the current player (in place of `replaces`, if given); the hour the server has. */
async function save(sub: PushSubscription, hour?: number, replaces?: string): Promise<number> {
  const { endpoint, keys } = sub.toJSON()
  const saved = await api.post<Reminder>('/me/reminder', {
    subscription: { endpoint, keys: { p256dh: keys?.p256dh, auth: keys?.auth } },
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    hour,
    replaces,
  })
  storage.set(HOUR_KEY, String(saved.hour))
  if (player) storage.set(SYNCED_KEY, `${today()} ${player}`)
  return saved.hour
}

/** Works out where this browser stands, refreshing the saved reminder when it's due. */
function load(): Promise<void> {
  return (loading ??= (async () => {
    config ??= api.get<ReminderConfig>('/reminders').then((c) => c.publicKey, () => null)
    const key = await config
    if (!key) return void set({ kind: 'none' })
    if (!canPush()) return void set({ kind: isIos() && !isStandalone() ? 'needs-install' : 'none' })
    const reg = await worker
    if (!reg) return void set({ kind: 'none' })
    registration = reg
    publicKey = key
    if (Notification.permission === 'denied') return void set({ kind: 'blocked' })
    const sub = await reg.pushManager.getSubscription()
    if (!sub || Notification.permission !== 'granted') return void set({ kind: 'off' })
    const hour = savedHour()
    set({ kind: 'on', hour: hour ?? DEFAULT_HOUR })
    if (!madeWith(sub, key)) {
      // The server has new keys, so pushes to this subscription are refused: make one with them
      // in its place. Safari may want a tap for that; then the reminder is off until they turn it on.
      try {
        await sub.unsubscribe()
        set({ kind: 'on', hour: await save(await subscribeWith(reg, key), hour ?? undefined, sub.endpoint) })
      } catch {
        await api.del('/me/reminder', { endpoint: sub.endpoint }).catch(() => {})
        set({ kind: 'off' })
      }
      return
    }
    if (player && storage.get(SYNCED_KEY) !== `${today()} ${player}`) {
      await save(sub, hour ?? undefined).then((h) => set({ kind: 'on', hour: h }), () => {})
    }
  })())
}

/** After /me loads, when the player changes (signing in or out), and back in view: refreshes a reminder that's on. */
export function refreshReminder(playerId: string) {
  player = playerId
  if (!canPush() || Notification.permission !== 'granted') return
  loading = null
  void load()
}

export function useReminder(): ReminderState {
  const s = useSyncExternalStore(subscribe, () => state)
  useEffect(() => void load(), [])
  return s
}

/**
 * Subscribes (asking for permission) and saves the reminder. Call straight from a tap: Safari only
 * asks from one, so the subscribe comes first, before anything else awaits. Declined or blocked
 * comes back as off or blocked; a failed save throws.
 */
export async function turnOnReminder(hour?: number): Promise<ReminderState> {
  const reg = registration
  const key = publicKey
  if (!reg || !key) return state
  let sub: PushSubscription
  try {
    sub = await subscribeWith(reg, key)
  } catch {
    return set(Notification.permission === 'denied' ? { kind: 'blocked' } : { kind: 'off' })
  }
  try {
    return set({ kind: 'on', hour: await save(sub, hour ?? plannedHour()) })
  } catch (err) {
    await sub.unsubscribe().catch(() => {})
    throw err
  }
}

export async function setReminderHour(hour: number): Promise<ReminderState> {
  const sub = await registration?.pushManager.getSubscription()
  if (!sub) return turnOnReminder(hour)
  return set({ kind: 'on', hour: await save(sub, hour) })
}

/**
 * Unsubscribes here first: if that fails nothing has changed (and it throws). The server's copy
 * goes next; if that call fails, the push service's 410 for the dead subscription ends it.
 */
export async function turnOffReminder(): Promise<ReminderState> {
  const sub = await registration?.pushManager.getSubscription()
  if (sub) {
    if (!(await sub.unsubscribe())) throw new Error('Could not unsubscribe')
    await api.del('/me/reminder', { endpoint: sub.endpoint }).catch(() => {})
  }
  return set({ kind: 'off' })
}

/**
 * Settings' "Send a test": the API pushes one to this browser now. When the server doesn't have
 * this browser's reminder (it lost it, or it's saved for whoever played here before), it's saved
 * again first. A push service that says the subscription is gone leaves the reminder off here too.
 */
export async function sendTestReminder(): Promise<ReminderTest> {
  const sub = await registration?.pushManager.getSubscription()
  if (!sub) {
    set({ kind: 'off' })
    throw new Error('No subscription')
  }
  const test = () => api.post<ReminderTest>('/me/reminder/test', { endpoint: sub.endpoint })
  let result: ReminderTest
  try {
    result = await test()
  } catch (err) {
    if (!(err instanceof ApiFailure && err.status === 404)) throw err
    await save(sub, savedHour() ?? undefined)
    result = await test()
  }
  if (result.outcome === 'ended') {
    await sub.unsubscribe().catch(() => false)
    set({ kind: 'off' })
  }
  return result
}

/** Whether the service worker says push `id` arrived within `ms`. */
export function arrival(id: string, ms: number): Promise<boolean> {
  if (arrived.has(id)) return Promise.resolve(true)
  return new Promise((resolve) => {
    const done = (got: boolean) => {
      clearTimeout(timer)
      waiting.delete(id)
      resolve(got)
    }
    const timer = setTimeout(() => done(false), ms)
    waiting.set(id, () => done(true))
  })
}

/** "9:00 AM", or "09:00" where clocks run 24 hours. */
export const formatHour = (hour: number) => new Date(2000, 0, 1, hour).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
